<template>
    <GDialog
        v-model="modalStore.activeRoutePointModal.show"
        max-width="500"
        border-radius="10"
        @update:model-value="onModelUpdate"
    >
        <div class="active-route-point-modal">
            <div class="container">
                <div class="modal-header">
                    <div class="modal-title">
                        <div
                            class="route-status"
                            :style="{ background: pointData?.status?.color }"
                        ></div>
                        <div class="text-h4 text-bold">{{ pointAddress }}</div>
                    </div>
                    <button
                        v-if="sortedItems.length > 1"
                        class="sort-btn"
                        @click="toggleSort"
                        :title="sortDirection === 'asc' ? 'Сортування за зростанням' : 'Сортування за спаданням'"
                    >
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            :class="{ 'rotated': sortDirection === 'desc' }"
                        >
                            <path
                                d="M10 3L10 17M10 17L5 12M10 17L15 12"
                                stroke="currentColor"
                                stroke-width="1.5"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>
                    </button>
                </div>

                <div class="modal-body">
                    <div class="route-point-body">

                        <div class="task-type-filter select-group" v-if="availableTypes.length > 0">
                            <div class="label text-14 text-bold">Тип завдання</div>
                            <Multiselect
                                v-model="activeFilters"
                                :options="availableTypeOptions"
                                :close-on-select="false"
                                :clear-on-select="false"
                                :searchable="false"
                                value-prop="key"
                                label="label"
                                track-by="key"
                                placeholder="Всі типи"
                                mode="tags"
                            />
                        </div>

                        <template v-if="pointData?.itemsLoading && !pointData?.items?.length">
                            <div class="no-tasks text-h4 text-grey">Завантаження...</div>
                        </template>
                        <template v-else-if="filteredItems.length">
                            <ActiveRouteTask
                                v-for="(task, key) in filteredItems"
                                :key="key"
                                :task-data="task"
                            />
                        </template>
                        <template v-else>
                            <div class="no-tasks text-h4 text-grey">
                                {{
                                    activeFilters.length
                                        ? 'Немає завдань за обраним фільтром'
                                        : 'У вас немає завдань на цій точці'
                                }}
                            </div>
                        </template>

                        <ButtonComponent
                            v-if="
                                pointData &&
                                pointData.status.value !== 'Completed' &&
                                firstUncompleted
                            "
                            btn-style="gradient"
                            text="Позначити точку виконаною"
                            @click="completePoint"
                        />
                        <ButtonComponent
                            v-if="
                                pointData &&
                                pointData.status.value !== 'Uncompleted' &&
                                lastCompleted
                            "
                            btn-style="grey"
                            text="Позначити точку невиконаною"
                            @click="uncompletePoint"
                        />
                    </div>
                </div>

                <div class="modal-buttons">
                    <div class="container">
                        <ButtonComponent
                            class="back-btn"
                            btn-style="grey"
                            text="Повернутись назад"
                            @click="hideModal"
                        />
                    </div>
                </div>
            </div>
        </div>
    </GDialog>
</template>

<script>
import Multiselect from '@vueform/multiselect'
import ActiveRouteTask from '@/components/activeRoute/ActiveRouteTask.vue'
import ButtonComponent from '@/components/ButtonComponent.vue'
import { useActiveRouteStore, useModalStore } from '@/stores/index.js'

export default {
    name: 'ActiveRoutePointModal',
    components: { ButtonComponent, ActiveRouteTask, Multiselect },
    setup() {
        const activeRouteStore = useActiveRouteStore()
        const modalStore = useModalStore()

        return { activeRouteStore, modalStore }
    },
    data() {
        return {
            activeFilters: [],
            sortDirection: 'asc',
            typeLabels: {
                start_trip: 'Посадка',
                finish_trip: 'Висадка',
                start_transfer: 'Пересадка/Посадка',
                finish_transfer: 'Пересадка/Висадка',
                return_action: 'Не забрана посилка',
                retry_action: 'Посилка завтрашнього дня'
            }
        }
    },
    computed: {
        pointData() {
            if (!this.modalStore.activeRoutePointModal.pointId) return null

            return this.activeRouteStore.routeData.points?.find(
                (point) => point.id === this.modalStore.activeRoutePointModal.pointId
            )
        },

        sortedItems() {
            if (!this.pointData?.items) return []

            return [...this.pointData.items].sort((a, b) => {
                const cmp = this.compareInternalIds(a, b)

                return this.sortDirection === 'asc' ? cmp : -cmp
            })
        },

        availableTypes() {
            if (!this.sortedItems.length) return []

            const order = [
                'start_trip',
                'finish_trip',
                'start_transfer',
                'finish_transfer',
                'return_action',
                'retry_action'
            ]
            const found = new Set(this.sortedItems.map((i) => i.action_type))

            return order.filter((t) => found.has(t))
        },

        availableTypeOptions() {
            return this.availableTypes.map((type) => ({
                key: type,
                label: this.typeLabels[type] || type
            }))
        },

        filteredItems() {
            if (!this.activeFilters.length) return this.sortedItems

            return this.sortedItems.filter((i) => this.activeFilters.includes(i.action_type))
        },

        firstUncompleted() {
            return this.modalStore.activeRoutePointModal.firstUncompleted
        },

        lastCompleted() {
            return this.modalStore.activeRoutePointModal.lastCompleted
        },

        pointAddress() {
            return this.pointData?.address || 'Точка маршруту'
        }
    },
    watch: {
        'modalStore.activeRoutePointModal.show'(val) {
            if (!val) {
                this.activeFilters = []
                this.sortDirection = 'asc'
            }
        }
    },
    methods: {
        parseInternalId(id) {
            if (!id || String(id).trim() === '') {
                return { isEmpty: true }
            }

            const str = String(id).trim()

            if (/^\d+$/.test(str)) {
                return {
                    isEmpty: false,
                    isPureNumber: true,
                    numericValue: parseInt(str, 10),
                    parts: [str]
                }
            }

            const segments = str.split('-')
            const firstSegment = segments[0]
            const isNumericPrefix = /^\d+$/.test(firstSegment)

            return {
                isEmpty: false,
                isPureNumber: false,
                hasPrefix: true,
                isNumericPrefix,
                parts: segments
            }
        },

        comparePart(a, b) {
            const aNum = parseInt(a, 10)
            const bNum = parseInt(b, 10)
            const aIsNum = !isNaN(aNum) && String(aNum) === String(a).trim()
            const bIsNum = !isNaN(bNum) && String(bNum) === String(b).trim()

            if (aIsNum && bIsNum) return aNum - bNum
            if (aIsNum) return -1
            if (bIsNum) return 1

            return String(a).localeCompare(String(b))
        },

        compareInternalIds(itemA, itemB) {
            const a = this.parseInternalId(itemA.internal_id)
            const b = this.parseInternalId(itemB.internal_id)

            if (a.isEmpty && b.isEmpty) {
                return (itemA.id ?? 0) - (itemB.id ?? 0)
            }
            if (a.isEmpty) return 1
            if (b.isEmpty) return -1

            const getGroup = (parsed) => {
                if (parsed.isPureNumber) return 2
                if (parsed.isNumericPrefix) return 0

                return 1
            }

            const groupA = getGroup(a)
            const groupB = getGroup(b)

            if (groupA !== groupB) return groupA - groupB

            if (a.isPureNumber && b.isPureNumber) {
                return a.numericValue - b.numericValue
            }

            const maxLen = Math.max(a.parts.length, b.parts.length)

            for (let i = 0; i < maxLen; i++) {
                const partA = a.parts[i] ?? ''
                const partB = b.parts[i] ?? ''
                const cmp = this.comparePart(partA, partB)

                if (cmp !== 0) return cmp
            }

            return 0
        },

        toggleSort() {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
        },

        completePoint() {
            if (!this.pointData) return
            this.activeRouteStore.updatePointStatus(this.pointData.id, 'Completed')
        },

        uncompletePoint() {
            if (!this.pointData) return
            this.activeRouteStore.updatePointStatus(this.pointData.id, 'Uncompleted')
        },

        hideModal() {
            this.modalStore.activeRoutePointModal.show = false
            this.modalStore.activeRoutePointModal.pointId = null
            this.modalStore.activeRoutePointModal.firstUncompleted = false
            this.modalStore.activeRoutePointModal.lastCompleted = false
        },

        onModelUpdate(val) {
            if (!val) {
                this.hideModal()
            }
        }
    }
}
</script>

<style scoped lang="scss">
.active-route-point-modal {
    .modal-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .modal-title {
        display: flex;
        align-items: center;
        padding: 2rem 1rem 0 0;

        .route-status {
            margin-right: 0.5rem;
            width: 0.75rem;
            min-width: 0.75rem;
            height: 0.75rem;
            border-radius: 50%;
        }
    }

    .sort-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        margin-top: 2rem;
        width: 2rem;
        height: 2rem;
        min-width: 2rem;
        border: none;
        background: transparent;
        cursor: pointer;
        color: #9CA3AF;
        border-radius: 6px;
        transition: color 0.15s, background 0.15s;
        flex-shrink: 0;
        margin-bottom: 10px;
        margin-right: 5px;

        &:hover {
            color: #4F46E5;
            background: #F3F4F6;
        }

        svg {
            transition: transform 0.2s ease;

            &.rotated {
                transform: rotate(180deg);
            }
        }
    }

    .route-point-body {
        padding-right: 1rem;

        .button-component {
            font-size: 0.875rem;
            min-height: 2.5rem;
            padding: 0.4rem 0.5rem;
            margin-top: 1rem;
            width: 100%;
        }
    }

    .modal-body {
        padding: 2rem 0 10rem;
        overflow-y: auto;
    }

    .modal-buttons {
        .back-btn {
            width: 100% !important;
        }
    }

    .task-type-filter {
        margin-bottom: 1rem;

        .label {
            margin-bottom: 0.375rem;
        }
    }
}
</style>